import { Vec2, Vec3 } from 'ayvri-std/lib/vector';
import { defaultValue } from './helpers';

class HudElement {
  _id = undefined; // A numeric ID which can be used to reference the corresponding HUD element in Globe
  _updateFunc = undefined; // Called by all set*() functions to update the Globe HUD element
  _name = undefined; // For debugging purposes it's useful to have a human readable name

  _canvas = undefined; // An HTMLCanvasElement to draw
  _show = true; // If show is false, this element will not be drawn
  _scale = new Vec2(1, 1); // The element can be scaled from its original dimensions (in world or screen space)
  _worldPos = new Vec3(1, 0, 0); // The world space position of the anchor point
  _anchor = new Vec2(0, 1); // The anchor point in canvas space, from -1 to 1 where [0,1] is the canvas bottom center.
  _offset = new Vec2(0, 0); // Offsets the anchor point from worldPos in screen space. Units are pixels, +Y move down.
  _worldHeight = 0; // If set, the element is always x meters tall. Otherwise it is a constant screenspace size.
  _opacity = 1.0; // 1 is fully opaque, 0 is fully transparent.

  constructor(id, name, updateFunc) {
    this._id = id;
    this._updateFunc = updateFunc;
    this._name = defaultValue(name, 'Unknown');
  }

  setCanvas(canvas) {
    this._canvas = canvas;
    this._updateFunc({ canvas });
  }

  setWorldPos(pos) {
    this._worldPos.x = pos.x;
    this._worldPos.y = pos.y;
    this._worldPos.z = pos.z;
    this._updateFunc({ posWorld: this._worldPos });
  }

  setShow(show) {
    this._show = show;
    this._updateFunc({ show });
  }

  setScale(scale) {
    this._scale.x = scale;
    this._scale.y = scale;
    this._updateFunc({ scale: this._scale });
  }

  setScaleX(scaleX) {
    this._scale.x = scaleX;
    this._updateFunc({ scale: this._scale });
  }

  setScaleY(scaleY) {
    this._scale.y = scaleY;
    this._updateFunc({ scale: this._scale });
  }

  setAnchor(x, y) {
    this._anchor.x = x;
    this._anchor.y = y;

    if (this._canvas) {
      this._updateFunc({
        anchorOffset: {
          x: -(x * 0.5 + 0.5),
          y: -(y * 0.5 + 0.5),
        },
      });
    }
  }

  setOffset(x, y) {
    this._offset.x = x;
    this._offset.y = y;
    this._updateFunc({ offset: this._offset });
  }

  setWorldHeight(heightInMeters) {
    this._worldHeight = heightInMeters;
    this._updateFunc({ worldHeight: heightInMeters });
  }

  setOpacity(opacity) {
    this._opacity = opacity;
    this._updateFunc({ opacity });
  }

  setZScale(zScale) {
    this._zScale = zScale;
    this._updateFunc({ zScale });
  }

  get name() {
    return this._name;
  }

  get scale() {
    return this._scale;
  }

  get opacity() {
    return this._opacity;
  }

  get worldPos() {
    return this._worldPos;
  }

  get worldHeight() {
    return this._worldHeight;
  }

  get canvasWidth() {
    return this._canvas.width;
  }

  get canvasHeight() {
    return this._canvas.height;
  }
}

/**
 * HUD is responsible for managing 2D elements on screen. Elements may have either a world or screenspace position, and
 * they will be transformed into screenspace in HUD so that overlapping elements can be managed. All elements will
 * generate a single canvas which will be blitted onto the screen by the rendering backend.
 */
export default class Hud {
  constructor() {
    this.elements = [];
    this.enabled = false;
    this.elementIdCount = 1;
  }

  onSceneLoad(ui, globes) {
    this.globes = globes;
  }

  update() {}

  createElement(name) {
    const elementId = this.elementIdCount++;
    const updateFunc = options => this.globes.forEach(g => g.updateHudElement(elementId, options));
    const element = new HudElement(elementId, name, updateFunc);
    this.elements.push(element);

    this.globes.forEach(g => g.createHudElement(elementId, name));

    return element;
  }

  removeElement(element) {
    const idx = this.elements.indexOf(element);
    if (idx === -1) throw new Error(`HUD element does not exist: ${element}`);

    this.globes.forEach(g => g.deleteHudElement(element._id));
    this.elements.splice(idx, 1);
  }
}
